import { ReactComponent as InforIcon } from "../../shared/assets/informationLogo.svg";
import { themeAtom } from "../../shared/atoms";
import Input from "../../shared/components/Input";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import type { TechFormPageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import {
  Tq7SlowSteamingM,
  mapSqlToTq7SlowSteamingForm,
  tq7SlowSteamingFormInit,
} from "../models/tq7-slow-steaming.models";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import { tq7SlowStreamingAtom } from "../state/tech-form.atoms";
import { sanitizeTechFormInputForField, validateTq7SlowSteaming } from "../utils/tech-form-validations";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

export const Tq7SlowSteaming: React.FC<TechFormPageProps> = ({ isCompletedForm, cacheInitialForm, onSectionReady }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(tq7SlowStreamingAtom);

  const [form, setForm] = useState<Tq7SlowSteamingM>(tq7SlowSteamingFormInit);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof Tq7SlowSteamingM, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const { imoNumber, vesselName } = useParams();

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        cacheInitialForm(_form);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm, imoNumber, sectionLoadAttempt, vesselName]);

  useEffect(() => {
    setFormErrors(validateTq7SlowSteaming(form));
  }, [form]);

  const fetchData = async (isStale: () => boolean) => {
    if (!imoNumber || !vesselName) return;

    try {
      const requestIdentity = {
        imoNumber,
        vesselName,
        mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
      };
      if (!isTechFormSectionDataCached({ ...requestIdentity, section: 7 })) {
        setIsLoading(true);
      }
      const result = await loadTechFormEndpointData<any[]>({
        ...requestIdentity,
        endpoint: TECH_FORM_ENDPOINTS.slowSteaming,
      });
      if (isStale()) return;
      const _slowSteaming = result[0];
      const slowSteaming = _slowSteaming ? mapSqlToTq7SlowSteamingForm(_slowSteaming) : { ...tq7SlowSteamingFormInit };
      setForm(slowSteaming);
      setFormErrors(validateTq7SlowSteaming(slowSteaming));
      setStoredForm(slowSteaming);
      cacheInitialForm(slowSteaming);
      setIsSectionDataReady(true);
      onSectionReady?.();
    } catch (error: unknown) {
      if (!isStale()) {
        setSectionLoadError(true);
        console.error("Unable to load Technical Form slow-steaming data", error);
      }
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeTechFormInputForField(name, value);

    setForm((prevState) => {
      const nextForm = { ...prevState, [name]: sanitizedValue };
      setFormErrors(validateTq7SlowSteaming(nextForm));
      return nextForm;
    });

    setStoredForm({
      ...form,
      [name]: sanitizedValue,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors(validateTq7SlowSteaming(form));
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm, form, isSectionDataReady]);

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
        )}
      >
        <h1 className="custom-heading mb-7">Main Engine Info & Slow Steaming</h1>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-5 gap-4">
            <div data-test="propeller-pitch" className="w-full">
              <Input
                type="text"
                inputMode="decimal"
                name="propellerPitch"
                value={form.propellerPitch}
                onChange={handleInputChange}
                topLabel="Propeller Pitch"
                suffix="m"
                error={formErrors.propellerPitch}
              />
            </div>

            <div data-test="propeller-diameter" className="w-full">
              <Input
                type="text"
                inputMode="decimal"
                name="propellerDiameter"
                value={form.propellerDiameter}
                onChange={handleInputChange}
                topLabel="Propeller Diameter"
                suffix="m"
                error={formErrors.propellerDiameter}
              />
            </div>

            <div data-test="main-engine-maker-model" className="w-full">
              <Input
                type="text"
                name="mainEngineMakerModel"
                value={form.mainEngineMakerModel}
                onChange={handleInputChange}
                topLabel="Main Engine Maker and model"
                error={formErrors.mainEngineMakerModel}
              />
            </div>

            <div data-test="engine-tuning" className="w-full">
              <Input
                type="text"
                name="engineTuning"
                value={form.engineTuning}
                onChange={handleInputChange}
                topLabel="Engine Tuning"
                error={formErrors.engineTuning}
                bottomLabel="If applicable"
              />
            </div>

            <div data-test="nox-tier-iii-compliance-method" className="w-full">
              <Input
                type="text"
                name="noxTierIIIComplianceMethod"
                value={form.noxTierIIIComplianceMethod}
                onChange={handleInputChange}
                topLabel="Please specify the NOx Tier III compliance method (hpSCR / lpSCR / EGR etc.)"
                error={formErrors.noxTierIIIComplianceMethod}
                bottomLabel="If applicable"
              />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div data-test="main-engine-smcr">
              <Input
                type="text"
                name="mainEngineSMCRRating"
                value={form.mainEngineSMCRRating}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Main Engine SMCR Rating</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-smcr"
                      data-tooltip-html="SMCR stand for specified maximum continuous rating"
                    />
                    <Tooltip id="tooltip-smcr" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.mainEngineSMCRRating}
                suffix="kW"
              />
            </div>

            <div data-test="main-engine-smcr-rating">
              <Input
                type="text"
                name="mainEngineMCRRating"
                value={form.mainEngineMCRRating}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Main Engine MCR Rating</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-mcr"
                      data-tooltip-html="MCR stand for Maximum Continuous Rating"
                    />
                    <Tooltip id="tooltip-mcr" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.mainEngineMCRRating}
                suffix="kW"
              />
            </div>

            <div data-test="main-engine-ncr">
              <Input
                type="text"
                name="mainEngineNCR"
                value={form.mainEngineNCR}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Main Engine NCR</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-ncr"
                      data-tooltip-html="usually about 78-85% SMCR"
                    />
                    <Tooltip id="tooltip-ncr" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.mainEngineNCR}
                suffix="kW"
                bottomLabel={"Normal Continuous Rating"}
              />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div data-test="engine-equipped-with-power-limited" className="w-full">
              <label className="block text-sm font-medium">Engine equipped with power limited EPL/ShaPoLi?</label>
              <div className="mt-3 flex items-center gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="engineEquippedWithPowerLimited"
                    value="yes"
                    checked={form.engineEquippedWithPowerLimited === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="engineEquippedWithPowerLimited"
                    value="no"
                    checked={form.engineEquippedWithPowerLimited === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            {form.engineEquippedWithPowerLimited === "yes" && (
              <>
                <div data-test="main-engine-limited-power-rating">
                  <Input
                    type="text"
                    name="mainEngineLimitedPowerRating"
                    value={form.mainEngineLimitedPowerRating}
                    onChange={handleInputChange}
                    topLabel="Main Engine Limited Power Rating"
                    error={formErrors.mainEngineLimitedPowerRating}
                    suffix="kW"
                  />
                </div>

                <div data-test="main-engine-rpm-at-limited-power">
                  <Input
                    type="text"
                    name="mainEngineRpmAtLimitedPower"
                    value={form.mainEngineRpmAtLimitedPower}
                    onChange={handleInputChange}
                    topLabel="RPM at limited power"
                    error={formErrors.mainEngineRpmAtLimitedPower}
                    suffix="RPM"
                  />
                </div>
              </>
            )}
          </div>

          <label className={twMerge("block text-sm font-medium", theme.headerComponent.inline.generalText)}>
            Main Engine RPM:
          </label>

          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4" data-test="smcr-rating">
              <Input
                type="text"
                inputMode="decimal"
                name="SMCRRating"
                value={form.SMCRRating}
                onChange={handleInputChange}
                topLabel={"@ MCR Rating"}
                error={formErrors.SMCRRating}
                suffix="RPM"
              />
            </div>

            <div className="mb-4" data-test="ncr-ballast">
              <Input
                type="text"
                inputMode="decimal"
                name="ncrBallast"
                value={form.ncrBallast}
                onChange={handleInputChange}
                topLabel="@ NCR (ballast)"
                error={formErrors.ncrBallast}
                suffix="RPM"
              />
            </div>

            <div className="mb-4" data-test="ncr-laden">
              <Input
                type="text"
                inputMode="decimal"
                name="ncrLaden"
                value={form.ncrLaden}
                onChange={handleInputChange}
                topLabel="@ NCR (laden)"
                error={formErrors.ncrLaden}
                suffix="RPM"
              />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4" data-test="critical-rpm-range-upper">
              <Input
                type="text"
                inputMode="decimal"
                name="criticalRPMUpper"
                value={form.criticalRPMUpper}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Critical RPM Range - Upper</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-upper"
                      data-tooltip-html="The upper end of the critical RPM range<br /> at which it is not possible to operate the vessel’s engine"
                    />
                    <Tooltip id="tooltip-upper" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.criticalRPMUpper}
                suffix="RPM"
              />
            </div>

            <div className="mb-4" data-test="critical-rpm-range-lower">
              <Input
                type="text"
                inputMode="decimal"
                name="criticalRPMLower"
                value={form.criticalRPMLower}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Critical RPM Range - Lower</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-lower"
                      data-tooltip-html="The lower end of the critical RPM range at which<br /> it is not possible to operate the vessel’s engine"
                    />
                    <Tooltip id="tooltip-lower" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.criticalRPMLower}
                suffix="RPM"
              />
            </div>

            <div className="mb-4" data-test="aux-blowers-cutoff-pressure">
              <Input
                type="text"
                inputMode="decimal"
                name="auxBlowersCutOFF"
                value={form.auxBlowersCutOFF}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Aux blowers cut-off pressure point</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-cutoff"
                      data-tooltip-html="The scavenge air receiver pressure cut-off<br /> setpoint in â€œAutoâ€ mode of blower"
                    />
                    <Tooltip id="tooltip-cutoff" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.auxBlowersCutOFF}
                suffix="Mpa"
              />
            </div>

            <div className="mb-4" data-test="aux-blowers-cutin-pressure">
              <Input
                type="text"
                inputMode="decimal"
                name="auxBlowersPressure"
                value={form.auxBlowersPressure}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Aux blowers cut-in pressure point</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-cutin"
                      data-tooltip-html="The scavenge air receiver pressure cut-in<br /> setpoint in â€œAutoâ€ mode of blower"
                    />
                    <Tooltip id="tooltip-cutin" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.auxBlowersPressure}
                suffix="Mpa"
              />
            </div>
          </div>

          <label className={twMerge("block text-sm font-semibold", theme.headerComponent.inline.generalText)}>
            Min Slow Steaming - Without aux blowers in operation
          </label>

          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4" data-test="min-slow-steaming-smcr">
              <Input
                type="text"
                inputMode="decimal"
                name="minSlowSMCR"
                value={form.minSlowSMCR}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Min slow steaming load %MCR</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-min-slow"
                      data-tooltip-html="Corresponds to continuous engine load just above<br /> aux blowers cut off point (usually about 38-50% SMCR)"
                    />
                    <Tooltip id="tooltip-min-slow" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.minSlowSMCR}
                suffix="%"
              />
            </div>

            <div className="mb-4" data-test="min-slow-steaming-engine">
              <Input
                type="text"
                inputMode="decimal"
                name="blocminSlowSteamingEngine"
                value={form.blocminSlowSteamingEngine}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Min slow steaming RPM</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-min-engine"
                      data-tooltip-html="rpm corresponding to Min Slow Steaming<br /> Main Engine %SMCR in good weather"
                    />
                    <Tooltip id="tooltip-min-engine" disableStyleInjection={true} />
                  </>
                }
                error={formErrors.blocminSlowSteamingEngine}
                suffix="RPM"
              />
            </div>

            <div className="mb-4" data-test="min-ultra-slow-smcr">
              <Input
                type="text"
                inputMode="decimal"
                name="minUltraSlowSMCR"
                value={form.minUltraSlowSMCR}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Super slow steaming load % MCR</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-ultra-slow"
                      data-tooltip-html="Min load the engine can maintain in case equipped<br /> for continuous operation below aux blowers cut-off point<br /> (usually about 15-35%SMCR)"
                    />
                    <Tooltip id="tooltip-ultra-slow" disableStyleInjection={true} />
                  </>
                }
                bottomLabel="If applicable"
                error={formErrors.minUltraSlowSMCR}
                suffix="%"
              />
            </div>
          </div>

          <label className={twMerge("block text-sm font-semibold", theme.headerComponent.inline.generalText)}>
            Super Slow Steaming - Aux Blowers in continuous operation (if applicable)
          </label>

          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4" data-test="min-ultra-slow-steaming-rpm">
              <Input
                type="text"
                inputMode="decimal"
                name="minUltraSlowStreamingRPM"
                value={form.minUltraSlowStreamingRPM}
                onChange={handleInputChange}
                topLabel={
                  <>
                    <span>Super Slow Steaming RPM</span>
                    <InforIcon
                      className="ml-1 inline-block h-3 w-3"
                      data-tooltip-id="tooltip-ultra-rpm"
                      data-tooltip-html="rpm corresponding to Min Ultra Slow Steaming<br /> Main Engine %SMCR in good weather"
                    />
                    <Tooltip id="tooltip-ultra-rpm" disableStyleInjection={true} />
                  </>
                }
                bottomLabel="If applicable"
                error={formErrors.minUltraSlowStreamingRPM}
                suffix="RPM"
              />
            </div>

            <div className="mb-4" data-test="vessel-fitted-energy-saving-devices">
              <label className="block text-sm font-medium">
                <span>Is the vessel fitted with energy saving devices?</span>
                <InforIcon
                  className="ml-1 inline-block h-3 w-3"
                  data-tooltip-id="tooltip-fitted"
                  data-tooltip-html="i.e. propeller boss cap fin, Mewis Duct, <br />performance monitor device of any kind<br /> or any other type of device/enhancement"
                />
                <Tooltip id="tooltip-fitted" disableStyleInjection={true} />
              </label>
              <div className="mt-3">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="isTheVesselFitted"
                    value="yes"
                    checked={form.isTheVesselFitted === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="isTheVesselFitted"
                    value="no"
                    checked={form.isTheVesselFitted === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.isTheVesselFitted && (
                <p className="mt-1 text-xs text-red-600">{formErrors.isTheVesselFitted}</p>
              )}
            </div>

            {form.isTheVesselFitted === "yes" && (
              <div className="col-span-2 mb-4" data-test="energy-saving-devices-remarks">
                <label className="block text-sm font-medium">Provide the details of energy saving devices:</label>
                <textarea
                  name="energySavingDevicesRemarks"
                  value={form.energySavingDevicesRemarks}
                  onChange={handleInputChange}
                  className={twMerge(
                    "mt-1 min-h-[96px] w-full rounded-md border p-2 focus:outline-none",
                    formErrors.energySavingDevicesRemarks ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                {formErrors.energySavingDevicesRemarks && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.energySavingDevicesRemarks}</p>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </LoadingWrapper>
  );
};
